import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Inject, Injectable, INJECTOR, Injector, Type } from '@angular/core';
import { SettingsDataLoadedGuard } from './data/settings-data-loaded.guard';
import { AppConfiguredGuard } from './app-config/app-config.guard';
import { lastValueFrom, Observable } from 'rxjs';

type GuardMethods = 'canActivate' | 'canActivateChild';

type GuardResult = boolean | UrlTree | Promise<boolean | UrlTree> | Observable<boolean | UrlTree>;

/**
 * Minimal shape shared by the sequenced guards. Route/state are optional
 * because these guards ignore them — this keeps the call site type-safe under
 * Angular 20's stricter `Injector.get` typings.
 */
interface SequentialGuard {
  canActivate(route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): GuardResult;
  canActivateChild(route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): GuardResult;
}

@Injectable({providedIn: 'root'})
export class AppInitializedGuard {
  /** These guards will be run sequentially until one of them returns false */
  private GUARDS_IN_ORDER: Type<SequentialGuard>[] = [
    AppConfiguredGuard,
    SettingsDataLoadedGuard,
  ];

  constructor(@Inject(INJECTOR) private readonly injector: Injector) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.runGuardsInSequence('canActivate', route, state);
  }

  async canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.runGuardsInSequence('canActivateChild', route, state);
  }

  /** Runs guard methods in provided order */
  private async runGuardsInSequence(guardMethod: GuardMethods, route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    for (const guard of this.GUARDS_IN_ORDER) {
      const instance = this.injector.get(guard);
      const guardResponse = instance[guardMethod](route, state);

      // Depending on the route result, we may need to await upon it in different ways.
      const result = await (guardResponse instanceof Observable ? lastValueFrom(guardResponse) : guardResponse);

      if (result !== true) {
        return result;
      }
    }

    return true;
  }
}
